import React, { useEffect, useRef } from 'react';
import { blogs } from '../mockData';
import { Heart, Bookmark, ExternalLink } from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import { motion } from 'framer-motion';
import VanillaTilt from 'vanilla-tilt';

const BlogSection = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const cardRefs = useRef([]);

  useEffect(() => {
    cardRefs.current.forEach((card) => {
      if (card) {
        VanillaTilt.init(card, {
          max: 5,
          speed: 400,
          glare: true,
          'max-glare': 0.2
        });
      }
    });

    return () => {
      cardRefs.current.forEach((card) => {
        if (card && card.vanillaTilt) {
          card.vanillaTilt.destroy();
        }
      });
    };
  }, []);

  return (
    <section id="blogs" className="min-h-screen flex items-center justify-center py-20 relative" ref={ref}>
      <div className="container mx-auto px-6">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Latest <span className="text-cyan-400">Blogs</span>
          </h2>
          <p className="text-gray-400 text-lg mb-4">
            Sharing my learnings and experiences in Cloud & DevOps
          </p>
          <div className="flex justify-center">
            <div className="w-20 h-1 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full"></div>
          </div>
        </motion.div>

        {/* Blog Cards Grid */}
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {blogs.map((blog, index) => (
            <motion.a
              key={blog.id}
              href={blog.url}
              target="_blank"
              rel="noopener noreferrer"
              ref={(el) => (cardRefs.current[index] = el)}
              className="group bg-gradient-to-br from-gray-900/50 to-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-xl overflow-hidden hover:border-cyan-400/50 transition-all duration-300 glow-on-hover block"
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              {/* Blog Thumbnail */}
              <div className="relative overflow-hidden h-48">
                <img
                  src={blog.thumbnail}
                  alt={blog.title}
                  width={800}
                  height={192}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute top-3 right-3 px-3 py-1 bg-cyan-400 text-black text-xs font-semibold rounded-full">
                  {blog.platform}
                </div>
              </div>

              {/* Blog Content */}
              <div className="p-6 space-y-4">
                {/* Author Info */}
                <div className="flex items-center gap-3">
                  <img
                    src={blog.author.avatar}
                    alt={blog.author.name}
                    width={40}
                    height={40}
                    loading="lazy"
                    className="w-10 h-10 rounded-full"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm truncate">
                      {blog.author.name}
                    </p>
                    <p className="text-gray-500 text-xs truncate">
                      {blog.author.domain}
                    </p>
                  </div>
                  <span className="text-gray-500 text-xs whitespace-nowrap">
                    {blog.publishedAt}
                  </span>
                </div>

                {/* Blog Title */}
                <h3 className="text-xl font-bold text-white group-hover:text-cyan-400 transition-colors duration-300 line-clamp-2">
                  {blog.title}
                </h3>

                {/* Blog Excerpt */}
                <p className="text-gray-400 text-sm leading-relaxed line-clamp-3">
                  {blog.excerpt}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-2">
                  {blog.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-gray-700/50 text-cyan-400 rounded-full text-xs font-medium"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                {/* Footer - Likes and Link */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-700/50">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1 text-gray-400">
                      <Heart size={16} />
                      <span className="text-xs">{blog.likes}</span>
                    </div>
                    <div className="flex items-center gap-1 text-gray-400">
                      <Bookmark size={16} />
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-cyan-400 text-sm font-medium">
                    Read More
                    <ExternalLink size={14} />
                  </div>
                </div>
              </div>
            </motion.a>
          ))}
        </div>

        {/* View All Blogs Button */}
        <motion.div 
          className="text-center mt-12"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <a
            href="https://hashnode.com/@krpavantej"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 bg-cyan-400 hover:bg-cyan-500 text-black font-semibold rounded-full transition-all duration-300 hover:scale-105 magnetic glow-on-hover"
          >
            View All Blogs
            <ExternalLink size={18} />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default BlogSection;
